/**
 * screeningIntegrity — deterministic consumption-integrity annotations for
 * Generation & Impact scenario data, applied at the ONE seam every
 * scenario-consuming report type shares (`reportProjection.ts`).
 *
 * Origin: the Redhead on-site power report review cycle (2026-07-27)
 * surfaced three recurring traps in how downstream reports read a frozen
 * scenario. Each helper below closes one of them, engine-level, so all
 * seven projection-carrying report types (feasibility, power-availability,
 * water, test-fit, environmental, public-sentiment, executive-summary)
 * inherit the fix without per-report patches:
 *
 *  1. VERDICT — the solver's headline verdict gates on the CRITICAL-LOAD
 *     test only; the reserve-padded requirement is a separate verdict. A
 *     scenario can read "pass" while missing the reserve bar (Redhead case
 *     A: +15.3 MW load margin, −29.7 MW reserve margin). Reports quoting
 *     the headline alone overstate. {@link resolveCapacityScreenVerdict}
 *     returns the JOINT screen verdict, naming that state 'conditional',
 *     with a print-ready statement.
 *
 *  2. DERATE BASIS — capacity verdicts are only comparable between
 *     scenarios screened on the same ambient × degradation basis. Redhead
 *     carried three bases across four scenarios; the pass/fail flipped
 *     with the basis while the technology ranking did not.
 *     {@link deriveScreeningBasis} produces the basis signature a report
 *     must print beside any verdict; {@link screeningBasesComparable}
 *     gates any cross-scenario comparison.
 *
 *  3. ANNUALIZATION — `result.gas/emissions/water` annual figures are a
 *     full-output capability basis (all installed capacity × operating
 *     hours), NOT energy served. Quoting capability CO2 to an offtaker as
 *     "expected annual" overstates by the utilization ratio (Redhead:
 *     ~2.3× against the screened facility load).
 *     {@link deriveAnnualizationBasis} labels the basis and carries the
 *     facility-load share, derived from FROZEN inputs only.
 *
 *  plus, for Test Fit:
 *
 *  4. FOOTPRINT CONVENTION — frozen scenarios keep their saved footprint
 *     intensity forever (replay rule), while the live planning convention
 *     evolves ({@link GENERATION_ISLAND_ACRES_PER_GROSS_MW}, re-sourced
 *     2026-07-25). {@link footprintConventionNote} flags a frozen basis
 *     that sits far outside the current convention band so a Test Fit
 *     reader reconciles the basis before land-take decisions — it never
 *     alters the frozen figures.
 *
 * Every helper is pure and reads only the frozen snapshot: nothing here
 * recomputes solver physics and nothing reads the live equipment catalog
 * (the convention band is a compile-time constant, imported as data).
 */
import type {
  EngineeringVerdict,
  GenerationAnnualizationBasisProjection,
  GenerationCapacityScreenVerdict,
  GenerationScreeningBasisProjection,
} from '../../types/generationImpact';
import { GENERATION_ISLAND_ACRES_PER_GROSS_MW } from './generationEquipmentCatalog';

/** Factor equality tolerance: frozen inputs are plain JSON numbers. */
const FACTOR_EPSILON = 1e-6;

/** Trigger band for the footprint-convention note (× outside the range). */
const FOOTPRINT_DIVERGENCE_FACTOR = 2;

export interface CapacityScreenResolution {
  /** Joint verdict with the reserve-padded requirement treated as binding. */
  screenVerdict: GenerationCapacityScreenVerdict;
  loadVerdict: EngineeringVerdict;
  reserveVerdict: EngineeringVerdict;
  /** Complete, print-ready sentence naming the binding test. */
  statement: string;
}

const fmtMW = (value: number): string => Math.abs(value).toFixed(1);

/**
 * Resolve the joint capacity-screen verdict. The reserve-padded requirement
 * is treated as the binding planning bar, consistent with the feasibility
 * screen's firm-coverage basis; a load-pass/reserve-fail scenario is
 * 'conditional', never a bare 'pass'.
 */
export function resolveCapacityScreenVerdict(args: {
  criterion: string;
  loadVerdict: EngineeringVerdict;
  reserveVerdict: EngineeringVerdict;
  loadMarginMW: number;
  reserveMarginMW: number;
}): CapacityScreenResolution {
  const { criterion, loadVerdict, reserveVerdict, loadMarginMW, reserveMarginMW } = args;
  let screenVerdict: GenerationCapacityScreenVerdict;
  let statement: string;
  if (loadVerdict === 'fail') {
    screenVerdict = 'fail';
    statement = `Fails the ${criterion} critical-load screen by ${fmtMW(loadMarginMW)} MW.`;
  } else if (loadVerdict === 'indeterminate' || reserveVerdict === 'indeterminate') {
    screenVerdict = 'indeterminate';
    statement = `The ${criterion} capacity screen is indeterminate on the frozen inputs.`;
  } else if (reserveVerdict === 'fail') {
    screenVerdict = 'conditional';
    statement = `Passes the ${criterion} critical-load test (+${fmtMW(loadMarginMW)} MW) but misses the `
      + `reserve-padded requirement by ${fmtMW(reserveMarginMW)} MW: conditional, not a full pass.`;
  } else {
    screenVerdict = 'pass';
    statement = `Passes the ${criterion} screen on both tests: +${fmtMW(loadMarginMW)} MW over the critical `
      + `load and +${fmtMW(reserveMarginMW)} MW over the reserve-padded requirement.`;
  }
  return { screenVerdict, loadVerdict, reserveVerdict, statement };
}

/** Minimal frozen-unit shape the basis derivation needs. */
export interface ScreeningBasisUnitInput {
  id: string;
  label?: string;
  ambientFactor?: number | null;
  degradationFactor?: number | null;
}

const asFactor = (value: number | null | undefined): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const fmtFactor = (value: number | null): string =>
  value == null ? '—' : String(Math.round(value * 10000) / 10000);

const pairLabel = (ambient: number | null, degradation: number | null): string =>
  `${fmtFactor(ambient)} × ${fmtFactor(degradation)}`;

/**
 * Derive the ambient × degradation screening basis of a frozen scenario.
 * The label is what a report must print beside any verdict; `uniform` is
 * false when units inside ONE scenario carry different bases (already a
 * red flag worth a note).
 */
export function deriveScreeningBasis(
  units: readonly ScreeningBasisUnitInput[],
): GenerationScreeningBasisProjection {
  const rows = units.map((unit) => ({
    unitId: unit.id,
    label: unit.label ?? unit.id,
    ambientFactor: asFactor(unit.ambientFactor),
    degradationFactor: asFactor(unit.degradationFactor),
  }));
  const pairs = new Map<string, string>();
  for (const row of rows) {
    pairs.set(pairLabel(row.ambientFactor, row.degradationFactor), pairLabel(row.ambientFactor, row.degradationFactor));
  }
  const uniform = pairs.size <= 1;
  const label = rows.length === 0
    ? 'unspecified'
    : uniform
      ? pairLabel(rows[0]!.ambientFactor, rows[0]!.degradationFactor)
      : `mixed (${[...pairs.keys()].join(' · ')})`;
  return {
    label,
    uniform,
    units: rows,
    ...(uniform ? {} : {
      note: 'Units inside this scenario carry different ambient × degradation bases; '
        + 'per-unit capacities are not directly comparable.',
    }),
  };
}

/**
 * Gate for cross-scenario comparisons: verdicts and capacities from two
 * scenarios are comparable only when both bases are uniform and equal.
 */
export function screeningBasesComparable(
  a: GenerationScreeningBasisProjection,
  b: GenerationScreeningBasisProjection,
): { comparable: boolean; note?: string } {
  const factorsEqual = (x: number | null, y: number | null): boolean =>
    x == null || y == null ? x === y : Math.abs(x - y) <= FACTOR_EPSILON;
  const aFirst = a.units[0];
  const bFirst = b.units[0];
  const comparable = a.uniform && b.uniform
    && aFirst != null && bFirst != null
    && factorsEqual(aFirst.ambientFactor, bFirst.ambientFactor)
    && factorsEqual(aFirst.degradationFactor, bFirst.degradationFactor);
  if (comparable) return { comparable: true };
  return {
    comparable: false,
    note: `Screening derate bases differ (${a.label} vs ${b.label}); capacity verdicts are not `
      + 'comparable without renormalizing to a common basis.',
  };
}

/**
 * Label the annualization basis of the frozen result and carry the
 * facility-load share, computed from frozen inputs only (no load-factor or
 * dispatch model is invented at report time).
 */
export function deriveAnnualizationBasis(args: {
  facilityLoadMW: number;
  annualOperatingHours: number;
  normalNetMW: number;
  gridImportOnly?: boolean;
}): GenerationAnnualizationBasisProjection {
  const hours = Number.isFinite(args.annualOperatingHours) && args.annualOperatingHours > 0
    ? args.annualOperatingHours
    : null;
  const normalNet = Number.isFinite(args.normalNetMW) && args.normalNetMW > 0 ? args.normalNetMW : null;
  const facilityLoad = Number.isFinite(args.facilityLoadMW) && args.facilityLoadMW >= 0
    ? args.facilityLoadMW
    : null;
  const screenedAnnualGWh = hours != null && normalNet != null ? (normalNet * hours) / 1000 : null;
  if (args.gridImportOnly === true) {
    // Grid feeds are a supply envelope, not simultaneously dispatched on-site
    // generators. The frozen operating hours already express the declared
    // aggregate-capacity-equivalent import duty. Do not apply load/capacity again.
    const usableDuty = screenedAnnualGWh != null && facilityLoad != null && facilityLoad > 0
      && screenedAnnualGWh <= facilityLoad * 8760 / 1000;
    return {
      basis: 'grid-import-duty',
      annualOperatingHours: hours ?? 0,
      screenedAnnualGWh,
      facilityLoadAnnualGWh: usableDuty ? screenedAnnualGWh : null,
      facilityLoadShare: usableDuty ? 1 : null,
      note: 'Annual imported-energy screen uses the declared aggregate-capacity-equivalent duty '
        + `(${normalNet ?? '—'} MW × ${hours ?? '—'} h/yr`
        + `${screenedAnnualGWh == null ? '' : ` = ${screenedAnnualGWh.toLocaleString('en-US')} GWh/yr`}). `
        + (usableDuty
          ? 'This is the declared grid-import load profile; do not scale it again by facility load / aggregate feed capacity. '
          : 'A facility-served annual duty is unresolved: the declared energy is missing or exceeds the facility peak over a full year. ')
        + 'Equivalent hours do not imply simultaneous dispatch of both feeds or utility availability. '
        + 'Zero on-site combustion does not imply zero upstream grid emissions.',
    };
  }
  const facilityLoadAnnualGWh = hours != null && facilityLoad != null ? (facilityLoad * hours) / 1000 : null;
  const facilityLoadShare = normalNet != null && facilityLoad != null ? facilityLoad / normalNet : null;
  const shareText = facilityLoadShare == null
    ? 'an indeterminate share'
    : `${Math.round(facilityLoadShare * 100)}% `
      + 'of that capability';
  const note = 'Annual gas, CO2 and water figures are a full-output capability basis '
    + `(all installed capacity × ${hours ?? '—'} h/yr`
    + `${screenedAnnualGWh == null ? '' : ` = ${Math.round(screenedAnnualGWh).toLocaleString('en-US')} GWh/yr`}). `
    + `Energy at the screened facility load is ${shareText}`
    + `${facilityLoadAnnualGWh == null ? '' : ` (${Math.round(facilityLoadAnnualGWh).toLocaleString('en-US')} GWh/yr)`}. `
    + 'Use capability figures for permit ceilings and peak sizing; scale by the served profile for '
    + 'offtake volumes and economics.';
  return {
    basis: 'full-output-capability',
    annualOperatingHours: hours ?? 0,
    screenedAnnualGWh,
    facilityLoadAnnualGWh,
    facilityLoadShare,
    note,
  };
}

/** Minimal frozen-result shape the footprint check needs. */
export interface FootprintConventionInput {
  plantAcres: number;
  unitResults: readonly { siteGrossMWPerUnit: number; count: number }[];
}

/**
 * Flag a frozen footprint intensity that sits far outside the CURRENT
 * generation-island planning convention band. Returns a note for the Test
 * Fit projection, or null when the frozen basis is within (or near) the
 * band. Never alters the frozen figures: the note exists precisely because
 * replayed footprints must not silently re-derive from the live catalog.
 */
export function footprintConventionNote(args: FootprintConventionInput): string | null {
  const totalSiteGrossMW = args.unitResults.reduce(
    (sum, row) => sum + (Number.isFinite(row.siteGrossMWPerUnit) && Number.isFinite(row.count)
      ? row.siteGrossMWPerUnit * row.count
      : 0),
    0,
  );
  if (!(totalSiteGrossMW > 0) || !Number.isFinite(args.plantAcres) || args.plantAcres <= 0) return null;
  const intensity = args.plantAcres / totalSiteGrossMW;
  const conventionValues = Object.values(GENERATION_ISLAND_ACRES_PER_GROSS_MW);
  const min = Math.min(...conventionValues);
  const max = Math.max(...conventionValues);
  if (intensity <= max * FOOTPRINT_DIVERGENCE_FACTOR && intensity >= min / FOOTPRINT_DIVERGENCE_FACTOR) {
    return null;
  }
  const fmt = (value: number): string => String(Math.round(value * 1000) / 1000);
  return `Frozen footprint intensity ${fmt(intensity)} ac per gross MW sits outside the current `
    + `generation-island planning convention (${fmt(min)}–${fmt(max)} ac/MW). Footprint figures reflect `
    + 'the frozen scenario basis; reconcile the basis before land-take decisions.';
}

/**
 * Tier-target availability shorthand (h/yr of allowed unavailability).
 * Industry shorthand figures (TIA-942 lineage), used only for perspective;
 * the Uptime Institute defines tiers by topology, not a number.
 */
const TIER_SHORTHAND_HOURS: Record<string, number> = {
  III: 1.6,
  IV: 0.4,
};

export interface ReliabilityPerspective {
  /** (1 - PLS) × 8,760; null when the screening produced no PLS. */
  expectedUnservedHoursPerYear: number | null;
  /** Shorthand allowance for the declared tier target, when recognised. */
  tierShorthandHoursPerYear: number | null;
  note: string;
}

/**
 * Translate the screening's probability-of-load-served into hours a reader
 * can hold against a data-center availability target. Born from the sponsor
 * question "does all that even get you to the uptime target?": capacity
 * adequacy is not availability, and the screening's own numbers say an
 * islanded fleet alone does not demonstrate uptime targets.
 */
export function deriveReliabilityPerspective(args: {
  probabilityLoadServed: number | null;
  tierTarget?: string | null;
  gridImportOnly?: boolean;
}): ReliabilityPerspective {
  const pls = typeof args.probabilityLoadServed === 'number'
    && Number.isFinite(args.probabilityLoadServed)
    && args.probabilityLoadServed > 0 && args.probabilityLoadServed <= 1
    ? args.probabilityLoadServed
    : null;
  const expected = pls == null ? null : (1 - pls) * 8760;
  const tierKey = args.tierTarget?.toString().trim().toUpperCase() ?? '';
  const tierHours = TIER_SHORTHAND_HOURS[tierKey] ?? null;
  const lead = expected == null
    ? 'The screening produced no probability-of-load-served, so expected unserved hours cannot be stated.'
    : `Screening probability-of-load-served ${(pls! * 100).toFixed(2)}% implies ~${Math.round(expected)} h/yr `
      + 'of expected unserved load'
      + (tierHours != null
        ? ` vs the ~${tierHours} h/yr shorthand of a Tier ${tierKey} target`
        : '')
      + '.';
  if (args.gridImportOnly === true) {
    return {
      expectedUnservedHoursPerYear: expected,
      tierShorthandHoursPerYear: tierHours,
      note: `${lead} Capacity adequacy is not availability. This grid-feed screen assumes independent outages; `
        + 'shared utility, substation, protection, route and other common-cause failures remain unqualified. '
        + 'It does not establish firm utility service or data-center uptime.',
    };
  }
  const note = `${lead} Capacity adequacy is not availability: an islanded fleet alone does not demonstrate `
    + 'data-center uptime targets; a grid share or larger fleet carries the availability case.';
  return { expectedUnservedHoursPerYear: expected, tierShorthandHoursPerYear: tierHours, note };
}

export interface GasDemandPerspective {
  peakDthPerDay: number;
  /** Capability annual scaled by the facility-load share, per day. */
  servedAverageDthPerDay: number | null;
  note: string;
}

/**
 * Peak-vs-served gas framing for fuel conversations. Born from the Redhead
 * interconnect scoping, where the on-file ask was sized near the campus
 * average rather than any configuration's firm peak: interconnect and firm
 * transport are sized to the committed peak; offtake and commercial volumes
 * follow the served profile.
 */
export function deriveGasDemandPerspective(args: {
  peakDthPerDay: number;
  annualMmbtu: number;
  facilityLoadShare: number | null;
}): GasDemandPerspective | null {
  if (!Number.isFinite(args.peakDthPerDay) || args.peakDthPerDay <= 0) return null;
  const servedAverage = Number.isFinite(args.annualMmbtu) && args.annualMmbtu > 0
    && args.facilityLoadShare != null && Number.isFinite(args.facilityLoadShare)
    && args.facilityLoadShare > 0
    ? (args.annualMmbtu * args.facilityLoadShare) / 365
    : null;
  const fmtK = (value: number): string => `~${Math.round(value / 100) / 10}k`;
  const note = `Peak-day demand (${fmtK(args.peakDthPerDay)} Dth/d) sizes the interconnect and firm transport`
    + (servedAverage != null
      ? `; the served-profile average (${fmtK(servedAverage)} Dth/d) is the volume basis for offtake and `
        + 'commercial conversations. '
      : '. ')
    + 'Size pipeline asks to the committed configuration\'s firm peak, never to the average.';
  return { peakDthPerDay: args.peakDthPerDay, servedAverageDthPerDay: servedAverage, note };
}
